// Full-width truck-trip card matching the engine report layout: header with
// Departs/ETA chain + stats, Weight/Area bars, a manifest grouped by customer
// (JC) with rowspan, then the trailer packing SVG.

import { formatClock, stopColorMap } from "../lib/format";
import { useT, type MsgKey, type Vars } from "../lib/i18n";
import type {
  OptimizeResponseDTO,
  PalletDetailDTO,
  RouteFeatureDTO,
  ThreePLRouteDTO,
  TruckDTO,
} from "../types";
import { TrailerSvg } from "./TrailerSvg";

type T = (key: MsgKey, vars?: Vars) => string;

const TRUCK_FALLBACK = "#5C7A9C";
const THREEPL_FALLBACK = "#9C27B0";
const LB_PER_KG = 2.20462;

interface ManifestGroup {
  key: string;
  stopNumber: number | null;
  jc: string | null;
  eta: string | null;
  stopLabel: string;
  rows: PalletDetailDTO[];
}

// Group the manifest by (stop, customer JC) so each customer visit reads as one
// block — Stop / ETA / Customer cells rowspan across that customer's pallets.
// Stops with more than one customer get sub-labels (2a, 2b, …), matching the
// engine report.
function buildGroups(manifest: PalletDetailDTO[]): ManifestGroup[] {
  const rows = [...manifest].sort((a, b) => {
    const sa = a.stop_number ?? Number.MAX_SAFE_INTEGER;
    const sb = b.stop_number ?? Number.MAX_SAFE_INTEGER;
    if (sa !== sb) return sa - sb;
    const ja = a.jc ?? "";
    const jb = b.jc ?? "";
    if (ja !== jb) return ja.localeCompare(jb);
    return a.shipment_id.localeCompare(b.shipment_id);
  });

  const groups: ManifestGroup[] = [];
  for (const r of rows) {
    const key = `${r.stop_number}|${r.jc}`;
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.rows.push(r);
    else
      groups.push({
        key,
        stopNumber: r.stop_number,
        jc: r.jc,
        eta: r.eta_clock,
        stopLabel: "",
        rows: [r],
      });
  }

  const byStop = new Map<number, ManifestGroup[]>();
  for (const g of groups) {
    if (g.stopNumber == null) continue;
    const arr = byStop.get(g.stopNumber) ?? [];
    arr.push(g);
    byStop.set(g.stopNumber, arr);
  }
  for (const [stop, gs] of byStop) {
    gs.forEach((g, i) => {
      g.stopLabel = gs.length > 1 ? `${stop}${String.fromCharCode(97 + i)}` : String(stop);
    });
  }
  for (const g of groups) if (g.stopNumber == null) g.stopLabel = "—";
  return groups;
}

function etaChain(manifest: PalletDetailDTO[], t: T): string {
  const byStop = new Map<number, { zip: string; eta: string | null }>();
  for (const p of manifest) {
    if (p.stop_number !== null && !byStop.has(p.stop_number)) {
      byStop.set(p.stop_number, { zip: p.dest_zip, eta: p.eta_clock });
    }
  }
  const stops = [...byStop.entries()].sort((a, b) => a[0] - b[0]);
  if (!stops.length) return "";
  return t("truck.etaPrefix") + stops.map(([, s]) => (s.eta ? `${s.zip} (${s.eta})` : s.zip)).join(" → ");
}

function fmtFt(n: number | null): string {
  return n == null ? "—" : n.toFixed(1);
}

function twBadge(ok: boolean): React.CSSProperties {
  return { color: ok ? "var(--ok)" : "var(--warn)", fontWeight: ok ? 500 : 700, whiteSpace: "nowrap" };
}

function TimeWindowCell({ p }: { p: PalletDetailDTO }) {
  const t = useT();
  const badges: React.ReactNode[] = [];
  if (p.arrival_after_clock) {
    const ok = p.arrival_after_met !== false;
    badges.push(
      <div key="a" style={twBadge(ok)}>
        {ok ? "✓" : "✗"} {t("truck.twAfter", { t: p.arrival_after_clock })}
        {ok ? "" : ` ${t("truck.twEarly")}`}
      </div>,
    );
  }
  if (p.deadline_clock) {
    const ok = p.deadline_met !== false;
    badges.push(
      <div key="d" style={twBadge(ok)}>
        {ok ? "✓" : "✗"} {t("truck.twBy", { t: p.deadline_clock })}
        {ok ? "" : ` ${t("truck.twLate", { min: p.late_min })}`}
      </div>,
    );
  }
  return <td>{badges.length ? badges : "—"}</td>;
}

// "11:00 AM" → minutes since midnight (for placing the lunch divider).
function clockMin(c: string | null | undefined): number | null {
  if (!c) return null;
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)?/i.exec(c.trim());
  if (!m) return null;
  let h = parseInt(m[1], 10) % 12;
  if (m[3] && m[3].toUpperCase() === "PM") h += 12;
  return h * 60 + parseInt(m[2], 10);
}

function ManifestTable({
  groups,
  stopColors,
  lunchClock,
  lunchEndClock,
}: {
  groups: ManifestGroup[];
  stopColors: Map<number, string>;
  lunchClock?: string | null;
  lunchEndClock?: string | null;
}) {
  const t = useT();
  const hasWindows = groups.some((g) =>
    g.rows.some((p) => p.arrival_after_clock || p.deadline_clock),
  );
  const nCols = hasWindows ? 10 : 9;
  const lunchMin = clockMin(lunchClock);
  let rowNum = 0;
  let lunchDone = false;
  return (
    <table className="manifest">
      <thead>
        <tr>
          <th>#</th>
          <th>Stop</th>
          <th>ETA</th>
          <th>Customer (JC)</th>
          <th>Shipment</th>
          <th>ZIP</th>
          <th>Dims (ft)</th>
          <th>Weight</th>
          <th>Rotated</th>
          {hasWindows && <th>Time Window</th>}
        </tr>
      </thead>
      <tbody>
        {groups.flatMap((g, gi) => {
          const out: React.ReactNode[] = [];
          // Insert the lunch divider before the first customer whose ETA is
          // at/after the lunch start (matches the engine report).
          const groupEta = clockMin(g.eta);
          if (!lunchDone && lunchMin != null && groupEta != null && groupEta >= lunchMin) {
            out.push(
              <tr key={`lunch-${gi}`}>
                <td colSpan={nCols} style={lunchRow}>
                  {t("truck.lunch", { start: lunchClock ?? "", end: lunchEndClock ?? "" })}
                </td>
              </tr>,
            );
            lunchDone = true;
          }
          const color = g.stopNumber != null ? stopColors.get(g.stopNumber) : undefined;
          const groupBg = gi % 2 === 0 ? "rgba(0,0,0,0.025)" : undefined;
          g.rows.forEach((p, ri) => {
            rowNum += 1;
            out.push(
              <tr key={p.shipment_id} style={groupBg ? { background: groupBg } : undefined}>
                <td>{rowNum}</td>
                {ri === 0 && (
                  <>
                    <td
                      rowSpan={g.rows.length}
                      className={color ? "stop-cell" : undefined}
                      style={
                        color
                          ? ({ ["--stop-color" as string]: color } as React.CSSProperties)
                          : undefined
                      }
                    >
                      Stop {g.stopLabel}
                    </td>
                    <td rowSpan={g.rows.length}>{g.eta ?? "—"}</td>
                    <td rowSpan={g.rows.length} style={{ fontWeight: 600 }}>
                      {g.jc ?? "—"}
                    </td>
                  </>
                )}
                <td>
                  {p.shipment_id}
                  {p.delivery_group && (
                    <span className="group-badge" title={`Grouped: ${p.delivery_group}`}>
                      {" "}
                      ↔ {p.delivery_group}
                    </span>
                  )}
                </td>
                <td>{p.dest_zip}</td>
                <td>
                  {p.length_ft != null && p.width_ft != null
                    ? `${fmtFt(p.length_ft)}x${fmtFt(p.width_ft)}`
                    : "—"}
                </td>
                <td>{p.weight_kg.toFixed(0)} kg</td>
                <td>{p.rotated ? "Yes" : ""}</td>
                {hasWindows && <TimeWindowCell p={p} />}
              </tr>,
            );
          });
          return out;
        })}
      </tbody>
    </table>
  );
}

const lunchRow: React.CSSProperties = {
  padding: "6px 10px",
  background: "#FFF7E0",
  color: "#7A5A00",
  fontWeight: 600,
  borderTop: "1px dashed #C9A227",
  borderBottom: "1px dashed #C9A227",
};

function FillBars({ weightPct, areaPct }: { weightPct: number; areaPct: number }) {
  return (
    <div className="fill-bars">
      {[
        ["Weight", weightPct],
        ["Area", areaPct],
      ].map(([label, pctRaw]) => {
        const pct = Number(pctRaw) || 0;
        const over = pct > 100;
        return (
          <div className="bar-row" key={label}>
            <span className="bar-label">{label}</span>
            <div className="bar">
              <div
                className="bar-fill"
                style={{
                  width: `${Math.min(100, pct)}%`,
                  ...(over ? { background: "var(--warn)" } : {}),
                }}
              />
            </div>
            <span
              className="bar-pct"
              style={over ? { color: "var(--warn)", fontWeight: 700 } : undefined}
            >
              {pct.toFixed(0)}%
            </span>
          </div>
        );
      })}
    </div>
  );
}

// Per-trip constraint breaches, surfaced as warning banners. Empty for a
// normal optimizer plan (within every cap); the payload of the evaluate view.
function deviationBanners(truck: TruckDTO, t: T): string[] {
  const out: string[] = [];
  if (truck.weight_over_kg > 0) {
    out.push(
      t("truck.devWeight", {
        kg: truck.weight_over_kg.toFixed(0),
        pct: truck.weight_pct.toFixed(0),
      }),
    );
  } else if (truck.overweight) {
    out.push(t("truck.devOverweight"));
  }
  if (truck.over_capacity) {
    out.push(t("truck.devCapacity", { pct: truck.footprint_pct.toFixed(0) }));
  }
  if (truck.hours_over > 0) {
    out.push(
      t("truck.devHours", {
        h: (truck.hours ?? 0).toFixed(1),
        over: truck.hours_over.toFixed(1),
      }),
    );
  }
  if (truck.n_late > 0) {
    out.push(t("truck.devLate", { n: truck.n_late, min: truck.max_late_min }));
  }
  if (truck.n_early > 0) {
    out.push(t("truck.devEarly", { n: truck.n_early }));
  }
  return out;
}

export function TruckCard({
  truck,
  result,
}: {
  truck: TruckDTO;
  result: OptimizeResponseDTO;
}) {
  const t = useT();
  const color = truckColors(result).get(truck.truck_label) ?? TRUCK_FALLBACK;
  const trip = tripInfo(result).get(`${truck.truck_label}|${truck.trip_index}`);
  const groups = buildGroups(truck.manifest);
  const stopColors = stopColorMap(truck.manifest.map((p) => p.stop_number));
  const hours = result.summary.truck_hours?.[truck.truck_label];
  // When a truck runs more than one trip, the 12h cap applies to its whole
  // day (sum of trips) — flag it even if each trip alone is under.
  const truckTrips = result.trucks.filter(
    (t) => t.truck_label === truck.truck_label,
  ).length;
  const truckDayOver = truckTrips > 1 && (hours ?? 0) > 12;

  const annotations = {
    colorByShipmentId: Object.fromEntries(
      truck.manifest.map((p) => [
        p.shipment_id,
        p.stop_number !== null ? stopColors.get(p.stop_number) ?? "#D4A017" : "#D4A017",
      ]),
    ),
    stopByShipmentId: Object.fromEntries(
      truck.manifest
        .filter((p) => p.stop_number !== null)
        .map((p) => [p.shipment_id, p.stop_number as number]),
    ),
    destByShipmentId: Object.fromEntries(truck.manifest.map((p) => [p.shipment_id, p.dest_zip])),
  };

  const stats = [
    `${truck.manifest.length} pallets`,
    trip ? `${trip.miles.toFixed(0)} mi` : null,
    hours != null ? `${hours.toFixed(1)} h` : null,
    // 3PL trips are billed per trip at the 3PL rates — surface this trip's cost.
    truck.is_3pl && truck.cost_usd != null ? `$${truck.cost_usd.toFixed(0)} 3PL` : null,
    `${(truck.weight_kg * LB_PER_KG).toFixed(0)} lbs (${truck.weight_kg.toFixed(0)} kg)`,
  ].filter(Boolean);

  return (
    <article className="truck-card" style={{ ["--truck-color" as string]: color } as React.CSSProperties}>
      <header className="truck-header">
        <div>
          <span className="truck-label" style={{ color }}>
            {truck.is_3pl
              ? `${truck.truck_label.replace("3PL", "3PL ")} · Trip ${truck.trip_index + 1}`
              : t("truck.header", { label: truck.truck_label, n: truck.trip_index + 1 })}
            {truck.is_3pl && (
              <span className="group-badge" style={{ marginLeft: 6 }}>
                3PL
              </span>
            )}
          </span>
          {!truck.is_3pl && truck.depart_min != null && (
            <div className="truck-route-meta">
              {t("truck.departs", {
                load: formatClock(truck.depart_min - 60),
                depart: formatClock(truck.depart_min),
              })}
            </div>
          )}
          <div className="truck-route-meta">{etaChain(truck.manifest, t)}</div>
        </div>
        <span className="truck-meta">{stats.join(" · ")}</span>
      </header>
      <FillBars weightPct={truck.weight_pct} areaPct={truck.footprint_pct} />
      <ManifestTable
        groups={groups}
        stopColors={stopColors}
        lunchClock={truck.lunch_clock}
        lunchEndClock={truck.lunch_end_clock}
      />
      <TrailerSvg packing={truck.packing} annotations={annotations} />
      {truckDayOver && (
        <div className="banner warn">
          {t("truck.devTruckDay", {
            label: truck.truck_label,
            h: (hours ?? 0).toFixed(1),
            trips: truckTrips,
          })}
        </div>
      )}
      {deviationBanners(truck, t).map((b) => (
        <div key={b} className="banner warn">
          {b}
        </div>
      ))}
    </article>
  );
}

export function ThreePlCard({
  route,
  result,
}: {
  route: ThreePLRouteDTO;
  result: OptimizeResponseDTO;
}) {
  const t = useT();
  const color = threeplColors(result).get(route.route_id) ?? THREEPL_FALLBACK;
  const groups = buildGroups(route.pallet_details);
  const stopColors = stopColorMap(route.pallet_details.map((p) => p.stop_number));
  return (
    <article className="truck-card threepl" style={{ ["--truck-color" as string]: color } as React.CSSProperties}>
      <header className="truck-header">
        <span className="truck-label" style={{ color }}>
          {route.route_id}
        </span>
        <span className="truck-meta">
          {t("truck.threeplMeta", {
            stops: route.n_stops,
            mi: route.miles.toFixed(1),
            cost: route.cost_usd.toFixed(0),
          })}
        </span>
      </header>
      {route.weight_pct != null && (
        <FillBars weightPct={route.weight_pct} areaPct={route.footprint_pct ?? 0} />
      )}
      {groups.length > 0 && <ManifestTable groups={groups} stopColors={stopColors} />}
    </article>
  );
}

function truckColors(result: OptimizeResponseDTO): Map<string, string> {
  const out = new Map<string, string>();
  for (const f of result.routes.features) {
    const p = (f as RouteFeatureDTO).properties;
    if (p.kind === "own_truck" && p.truck_label && !out.has(p.truck_label)) out.set(p.truck_label, p.color);
  }
  return out;
}

function threeplColors(result: OptimizeResponseDTO): Map<string, string> {
  const out = new Map<string, string>();
  for (const f of result.routes.features) {
    const p = (f as RouteFeatureDTO).properties;
    if (p.kind === "threepl" && !out.has(p.route_id)) out.set(p.route_id, p.color);
  }
  return out;
}

function tripInfo(result: OptimizeResponseDTO): Map<string, { miles: number; n_stops: number }> {
  const out = new Map<string, { miles: number; n_stops: number }>();
  for (const f of result.routes.features) {
    const p = (f as RouteFeatureDTO).properties;
    if (p.kind === "own_truck" && p.truck_label !== null && p.trip_index !== null) {
      out.set(`${p.truck_label}|${p.trip_index}`, { miles: p.miles, n_stops: p.stops.length });
    }
  }
  return out;
}
